import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Globe, Clock, Users, Ticket, Navigation, Calendar, ArrowLeft, Instagram, Mail, FileText, Briefcase } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { ListingHeroImg } from "../lib/termImages";
import { fmtDate, fmtDateTime } from "../components/Cards";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { ListingActions } from "../components/ListingActions";
import { NominateButton, AwardBadges, VoteCard } from "../components/AwardsWidgets";
import { ReviewsSection } from "../components/Reviews";
import { Paged } from "../components/PagedList";
import { Sponsors } from "../components/Marketplace";
import { VideoGrid } from "../components/VideoGrid";
import { DealsSection, MerchGrid, TableBookingForm, PrivateEventForm } from "../components/VenueExtras";
import LoyaltyJoinCard from "../components/LoyaltyJoinCard";

const MAP = (q) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

function MenuTabs({ sections }) {
  const [active, setActive] = useState(0);
  const cur = sections[active] || sections[0];
  const showTabs = sections.length > 1;
  return (
    <div className="mt-8" data-testid="venue-menu-tabs">
      {showTabs && (
        <div className="flex flex-wrap gap-2 pb-1" role="tablist" data-testid="venue-menu-tablist">
          {sections.map((sec, i) => (
            <button
              key={`${sec.section_name || "section"}-${i}`}
              role="tab"
              aria-selected={i === active}
              onClick={() => setActive(i)}
              data-testid={`venue-menu-tab-${i}`}
              className={`font-display font-bold text-sm px-4 py-2 rounded-full border transition-colors ${
                i === active
                  ? "bg-rose-500 text-white border-rose-500"
                  : "bg-[#111] text-zinc-300 border-white/10 hover:border-white/30"
              }`}
            >
              {sec.section_name || `Section ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      <div className="mt-6 max-w-2xl space-y-4" data-testid={`venue-menu-panel-${active}`}>
        {!showTabs && <h3 className="font-display font-bold text-xl mb-2 text-rose-400">{cur.section_name}</h3>}
        {(cur.items || []).map((it, j) => (
          <div key={`${it.item_name || "item"}-${j}`} className="flex justify-between gap-4 border-b border-white/5 pb-3">
            <div>
              <p className="font-semibold">{it.item_name}</p>
              <p className="text-sm text-zinc-400">{it.item_description}</p>
            </div>
            <p className="font-display font-bold whitespace-nowrap">${it.price}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VenueDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  useEffect(() => { api.get(`/venues/${slug}`).then((r) => setV(r.data)).catch(() => setV(false)); window.scrollTo(0, 0); }, [slug]);
  useSeo(v ? v.seo_title || `${v.name} | Gulf Coast VIP` : null, v?.description);

  if (v === false) return <div className="py-40 text-center text-zinc-500">Venue not found.</div>;
  if (!v) return <div className="py-40 text-center text-zinc-500">Loading...</div>;

  const detail = (Icon, label, val) => {
    if (!val) return null;
    const l = label.toLowerCase();
    let href = null;
    if (l.includes("email")) href = `mailto:${val}`;
    else if (l.includes("phone")) href = `tel:${String(val).replace(/[^+\d]/g, "")}`;
    else if (l.includes("website")) href = /^https?:\/\//i.test(val) ? val : `https://${val}`;
    return (
      <div className="flex items-start gap-3" data-testid={`venue-detail-${l.replace(/\s+/g, "-")}`}>
        <Icon size={18} className="text-rose-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-zinc-500">{label}</p>
          {href ? (
            <a href={href} target={l.includes("website") ? "_blank" : undefined} rel={l.includes("website") ? "noreferrer" : undefined} className="text-sm text-rose-400 hover:text-rose-300 hover:underline break-words" data-testid={`venue-${l.replace(/\s+/g, "-")}-link`}>{val}</a>
          ) : (
            <p className="text-sm break-words">{val}</p>
          )}
        </div>
      </div>
    );
  };
  const social = v.social_links || {};

  return (
    <div data-testid="venue-detail" className="pb-24 lg:pb-0">
      {/* Hero */}
      <div className="relative min-h-[80vh] flex flex-col overflow-hidden grain">
        <ListingHeroImg ent={v} kind="venue" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(251,146,60,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/30" />
        <Link to="/venues" className="relative z-10 self-start mt-6 ml-6 md:ml-12 lg:ml-20 flex items-center gap-1 text-sm text-zinc-300 hover:text-white"><ArrowLeft size={16} /> All venues</Link>
        <div className="relative flex-1 px-6 md:px-12 lg:px-20 flex flex-col justify-end pb-12 pt-10">
          <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl">
            {v.logo && <img src={fileUrl(v.logo)} alt="" className="w-16 h-16 rounded-2xl object-cover mb-4 border border-white/15" />}
            <p className="overline mb-2">{v.venue_type}</p>
            <h1 className="font-display font-black text-4xl md:text-7xl leading-[0.95] text-shadow-lg">{v.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {v.vip && <span className="bg-amber-400 text-black text-xs font-extrabold px-3 py-1 rounded-full" data-testid="venue-vip-badge">VIP</span>}
              {v.spotlight_badge && <span className="bg-rose-500 text-white text-xs font-extrabold px-3 py-1 rounded-full" data-testid="venue-spotlight-badge">★ {v.spotlight_badge}</span>}
              {v.award_badges?.length > 0 && <AwardBadges badges={v.award_badges} />}
            </div>
            <p className="text-lg md:text-xl text-zinc-300 mt-3">{v.tagline}</p>
            <p className="text-zinc-300 mt-1 flex items-center gap-2"><MapPin size={16} className="text-rose-400" /> {v.city}, {v.state}</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href="#events" className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 font-bold px-5 py-2.5 rounded-full text-sm"><Calendar size={15} /> Upcoming events</a>
              {(v.menu?.length > 0 || v.menu_pdf || v.menu_image || v.menu_link) && <a href="#menu" className="flex items-center gap-2 glass px-5 py-2.5 rounded-full text-sm hover:border-white/30"><Ticket size={15} /> Menu</a>}
              <a href={MAP(v.address)} target="_blank" rel="noreferrer" className="flex items-center gap-2 glass px-5 py-2.5 rounded-full text-sm hover:border-white/30" data-testid="venue-directions"><Navigation size={15} /> Directions</a>
              {v.phone && <a href={`tel:${v.phone}`} className="flex items-center gap-2 glass px-5 py-2.5 rounded-full text-sm hover:border-white/30" data-testid="venue-call"><Phone size={15} /> Call</a>}
              {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 glass px-5 py-2.5 rounded-full text-sm hover:border-white/30" data-testid="venue-website"><Globe size={15} /> Website</a>}
              {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" className="flex items-center justify-center w-10 h-10 glass rounded-full hover:border-white/30"><Instagram size={16} /></a>}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3"><ListingActions entityType="venue" entityId={v.id} claimStatus={v.claim_status || "unclaimed"} verified={!!v.verified} /><NominateButton entityType="venue" entity={v} /></div>
          </motion.div>
        </div>
      </div>

      <div className="lg:hidden"><VoteCard entityType="venue" entity={v} /></div>

      <LoyaltyJoinCard entityType="venue" entityId={v.id} />

      <div className="px-6 md:px-12 lg:px-20 py-14 grid lg:grid-cols-3 gap-12 items-start">
          {/* MAIN COLUMN */}
          <div className="lg:col-span-2 space-y-16 min-w-0">
            {/* About */}
            <Reveal>
              <SectionTitle kicker="The venue" title={`Welcome to ${v.name}`} />
              <p className="text-zinc-300 leading-relaxed text-lg mt-6">{v.description}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
                {[["Capacity", v.capacity], ["Type", v.venue_type], ["Cuisine", Array.isArray(v.cuisine) ? v.cuisine.join(", ") : v.cuisine], ["Setting", v.indoor_outdoor], ["Age", v.age_policy]].map(([l, val]) => val && (
                  <div key={l} className="bg-[#111] border border-white/10 rounded-xl p-4"><p className="text-xs text-zinc-500">{l}</p><p className="font-display font-bold mt-1">{val}</p></div>
                ))}
              </div>
            </Reveal>

            {/* Deals */}
            {v.deals?.length > 0 && <Reveal><Paged items={v.deals} perKey="venue_deals_pp" perDefault={4} label="deals" testid="venue-deals-paged" render={(slice) => <DealsSection deals={slice} />} /></Reveal>}

            {/* Hiring — mobile only; desktop renders in sidebar */}
            {v.jobs?.length > 0 && (
              <Reveal className="lg:hidden">
                <SectionTitle kicker="Join the team" title="Open Positions" />
                <Paged items={v.jobs} perKey="venue_jobs_pp" perDefault={4} label="jobs" testid="venue-jobs-paged" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="venue-jobs">
                  {slice.map((j) => (
                    <Link key={j.id} to={`/hiring/${j.slug}`} className="bg-[#111] border border-white/10 rounded-2xl p-5 card-hover block" data-testid={`venue-job-${j.slug}`}>
                      <p className="overline !text-[10px] mb-1 flex items-center gap-1"><Briefcase size={11} /> {j.job_category || "Job"}</p>
                      <h4 className="font-display font-bold text-lg">{j.title}</h4>
                      <p className="text-sm text-zinc-400 mt-1">{[j.job_type, j.city, j.pay_range].filter(Boolean).join(" · ")}</p>
                    </Link>
                  ))}
                </div>
                )} />
              </Reveal>
            )}

            {/* Menu */}
            {(v.menu?.length > 0 || v.menu_pdf || v.menu_image || v.menu_link) && (
              <Reveal>
                <div id="menu">
                  <SectionTitle kicker="Food & Drink" title="Menu" />
                  {(v.menu_pdf || v.menu_link) && (
                    <div className="flex flex-wrap gap-3 mt-6" data-testid="venue-menu-assets">
                      {v.menu_pdf && <a href={fileUrl(v.menu_pdf)} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 font-bold px-5 py-2.5 rounded-full text-sm" data-testid="venue-menu-pdf"><FileText size={15} /> View full menu (PDF)</a>}
                      {v.menu_link && <a href={v.menu_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 glass px-5 py-2.5 rounded-full text-sm hover:border-white/30" data-testid="venue-menu-link"><Globe size={15} /> Menu online</a>}
                    </div>
                  )}
                  {v.menu_image && <img src={fileUrl(v.menu_image)} alt="Menu" className="w-full max-w-2xl rounded-2xl border border-white/10 mt-6" data-testid="venue-menu-image" />}
                  {v.menu?.length > 0 && <MenuTabs sections={v.menu} />}
                </div>
              </Reveal>
            )}

            {/* Events — mobile only; desktop renders in sidebar */}
            <Reveal className="lg:hidden">
              <div id="events">
                <SectionTitle kicker="What's on" title="Upcoming Events" />
                <Paged items={v.upcoming_events || []} perKey="venue_events_pp" perDefault={6} label="events" testid="venue-upcoming" render={(slice) => (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-6">
                  {slice.map((e) => (
                    <Link key={e.id} to={`/events/${e.slug}`} className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden card-hover">
                      <img src={fileUrl(e.profile_image || e.event_image) || "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg?w=600"} alt="" className="w-full h-36 object-cover" />
                      <div className="p-4"><p className="font-semibold">{e.title}</p><p className="text-xs text-zinc-400 flex items-center gap-1 mt-1"><Calendar size={11} /> {fmtDateTime(e.event_date, e.start_time)}</p></div>
                    </Link>
                  ))}
                </div>
                )} />
                {(!v.upcoming_events || v.upcoming_events.length === 0) && <p className="text-zinc-500 text-sm mt-2">No upcoming events scheduled.</p>}
              </div>
            </Reveal>

            {v.past_events?.length > 0 && (
              <Reveal className="lg:hidden">
                <SectionTitle kicker="History" title="Past Events" />
                <Paged items={v.past_events} perKey="venue_past_pp" perDefault={6} label="events" testid="venue-past" render={(slice) => (
                <div className="flex flex-wrap gap-3 mt-6">
                  {slice.map((e) => <Link key={e.id} to={`/events/${e.slug}`} className="bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm card-hover">{e.title} <span className="text-zinc-500">· {fmtDate(e.event_date)}</span></Link>)}
                </div>
                )} />
              </Reveal>
            )}

            {v.past_artists?.length > 0 && (
              <Reveal className="lg:hidden">
                <SectionTitle kicker="On this stage" title="Artists who performed here" />
                <Paged items={v.past_artists} perKey="venue_artists_pp" perDefault={8} label="artists" testid="venue-artists" render={(slice) => (
                <div className="flex flex-wrap gap-3 mt-6">
                  {slice.map((a) => <Link key={a.id} to={`/artists/${a.slug}`} className="flex items-center gap-2 bg-[#111] border border-white/10 rounded-full pl-1 pr-4 py-1 card-hover">{fileUrl(a.profile_image) ? <img src={fileUrl(a.profile_image)} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-rose-500/20 text-rose-300 flex items-center justify-center text-sm font-bold">{(a.name || "?")[0]}</span>}<span className="text-sm">{a.name}</span></Link>)}
                </div>
                )} />
              </Reveal>
            )}

            {/* Gallery */}
            {v.gallery_images?.length > 0 && (
              <Reveal>
                <SectionTitle kicker="In frame" title="Gallery" />
                <Paged items={v.gallery_images} perKey="venue_gallery_pp" perDefault={12} label="photos" testid="venue-gallery" render={(slice) => (
                <div className="columns-2 md:columns-3 gap-4 space-y-4 mt-6">
                  {slice.map((g, i) => <img key={`${g}-${i}`} src={fileUrl(g)} alt="" loading="lazy" className="w-full rounded-2xl break-inside-avoid" />)}
                </div>
                )} />
              </Reveal>
            )}

            {/* Merch (manual + connected marketplace products) */}
            {((v.merch?.length || 0) + (v.marketplace_merch?.length || 0)) > 0 && <Reveal><Paged items={[...(v.marketplace_merch || []), ...(v.merch || [])]} perKey="venue_merch_pp" perDefault={6} label="items" testid="venue-merch" render={(slice) => <MerchGrid items={slice} title="Venue Merch" />} /></Reveal>}

            {/* Videos */}
            {v.videos?.length > 0 && <Reveal><Paged items={v.videos} perKey="venue_videos_pp" perDefault={6} label="videos" testid="venue-videos-paged" render={(slice) => <VideoGrid items={slice} />} /></Reveal>}

            {/* Booking forms — only when the venue enabled them */}
            {(v.accepts_table_bookings || v.accepts_private_events) && (
              <div className="grid xl:grid-cols-2 gap-6" data-testid="venue-booking-forms">
                {v.accepts_table_bookings && <Reveal><TableBookingForm venueId={v.id} /></Reveal>}
                {v.accepts_private_events && <Reveal><PrivateEventForm venueId={v.id} /></Reveal>}
              </div>
            )}

            {/* Sponsors */}
            {v.sponsors?.length > 0 && <Reveal><Sponsors items={v.sponsors} /></Reveal>}
            <ReviewsSection entityType="venue" entityId={v.id} ownerUserId={v.owner_user_id} />

            {/* Map — mobile only; desktop renders in sidebar */}
            <Reveal className="lg:hidden">
              <SectionTitle kicker="Find us" title="Map & Directions" />
              <div className="mt-6 rounded-2xl overflow-hidden border border-white/10">
                <iframe title="map" src={MAP(v.address || `${v.city}, ${v.state}`)} className="w-full h-80 grayscale invert-[.9]" loading="lazy" />
              </div>
            </Reveal>
          </div>

          {/* SIDEBAR — content-heavy now, so no sticky (would hide lower widgets on tall screens) */}
          <aside className="space-y-6" data-testid="venue-contact">
            {/* 1. VIP Spotlight Awards (VoteCard) */}
            <div className="hidden lg:block"><VoteCard entityType="venue" entity={v} compact /></div>

            {/* 2. Upcoming Events — big date card + wide image thumbnail + pagination */}
            {(v.upcoming_events?.length || 0) > 0 && (
              <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="venue-side-upcoming">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="font-display font-bold text-lg">Upcoming Events</h3>
                  <Link to={`/events?venue=${v.slug}`} className="text-xs font-bold text-rose-400 hover:text-rose-300" data-testid="venue-side-upcoming-all">View All</Link>
                </div>
                <Paged items={v.upcoming_events} perKey="venue_side_events_pp" perDefault={3} label="events" testid="venue-side-upcoming-paged" render={(slice) => (
                  <ul className="space-y-3">
                    {slice.map((e) => {
                      const d = e.event_date ? new Date(`${e.event_date}T00:00:00`) : null;
                      const mon = d ? d.toLocaleString("en-US", { month: "short" }).toUpperCase() : "";
                      const day = d ? String(d.getDate()) : "";
                      const dow = d ? d.toLocaleString("en-US", { weekday: "short" }).toUpperCase() : "";
                      const img = fileUrl(e.profile_image || e.event_image);
                      const time = e.start_time ? (() => {
                        const m = String(e.start_time).match(/^(\d{1,2}):(\d{2})/);
                        if (!m) return e.start_time;
                        let h = parseInt(m[1], 10); const min = m[2];
                        const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
                        return `${h}:${min} ${ap}`;
                      })() : null;
                      return (
                        <li key={e.id}>
                          <Link to={`/events/${e.slug}`} className="flex items-stretch gap-3 group bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 hover:border-white/15 rounded-xl p-2.5 transition-colors" data-testid={`venue-side-event-${e.slug}`}>
                            <div className="w-14 shrink-0 flex flex-col items-center justify-center bg-white/[0.03] border border-white/10 rounded-lg py-2">
                              <p className="text-[10px] font-extrabold text-rose-400 leading-none tracking-wider">{mon}</p>
                              <p className="font-display font-extrabold text-2xl leading-none mt-1">{day}</p>
                              {dow && <p className="text-[9px] font-bold text-zinc-500 leading-none mt-1.5 tracking-wider">{dow}</p>}
                            </div>
                            <div className="min-w-0 flex-1 flex flex-col justify-center py-0.5">
                              <p className="font-display font-bold text-[15px] text-white group-hover:text-rose-300 leading-tight line-clamp-1">{e.title}</p>
                              {(e.music_genre || e.event_type) && <p className="text-xs text-zinc-400 mt-1 truncate">{e.music_genre || e.event_type}</p>}
                              {time && <p className="text-xs text-zinc-400 mt-0.5">{time}</p>}
                            </div>
                            {img ? (
                              <img src={img} alt="" loading="lazy" className="w-20 aspect-[4/3] rounded-lg object-cover border border-white/10 shrink-0" />
                            ) : (
                              <div className="w-20 aspect-[4/3] rounded-lg bg-rose-500/15 border border-white/10 flex items-center justify-center shrink-0"><Calendar size={20} className="text-rose-300" /></div>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )} />
                <Link to={`/events?venue=${v.slug}`} className="mt-4 block text-center text-sm font-bold border border-rose-500/40 hover:border-rose-500 hover:bg-rose-500/10 text-rose-300 hover:text-rose-200 rounded-full py-2.5 transition-colors" data-testid="venue-side-upcoming-viewall">View All Events</Link>
              </div>
            )}

            {/* 3. Artists Who Performed Here — 3-col landscape cards + pagination */}
            {v.past_artists?.length > 0 && (
              <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="venue-side-artists">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="font-display font-bold text-lg">Artists Who Performed Here</h3>
                  <Link to="/artists" className="text-xs font-bold text-rose-400 hover:text-rose-300 shrink-0 ml-2" data-testid="venue-side-artists-all">View All</Link>
                </div>
                <Paged items={v.past_artists} perKey="venue_side_artists_pp" perDefault={3} label="artists" testid="venue-side-artists-paged" render={(slice) => (
                  <div className="grid grid-cols-3 gap-2.5">
                    {slice.map((a) => (
                      <Link key={a.id} to={`/artists/${a.slug}`} className="group text-center" data-testid={`venue-side-artist-${a.slug}`}>
                        {fileUrl(a.profile_image) ? (
                          <img src={fileUrl(a.profile_image)} alt="" loading="lazy" className="w-full aspect-square rounded-full object-cover border-2 border-white/10 group-hover:border-rose-500/60 transition-colors" />
                        ) : (
                          <div className="w-full aspect-square rounded-full bg-rose-500/15 border-2 border-white/10 text-rose-300 flex items-center justify-center text-2xl font-bold">{(a.name || "?")[0]}</div>
                        )}
                        <p className="text-xs font-semibold mt-2 text-zinc-200 group-hover:text-white leading-tight line-clamp-2">{a.name}</p>
                      </Link>
                    ))}
                  </div>
                )} />
              </div>
            )}

            {/* 4. Open Positions — with pagination */}
            {v.jobs?.length > 0 && (
              <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="venue-side-jobs">
                <h3 className="font-display font-bold text-lg mb-4">Open Positions</h3>
                <Paged items={v.jobs} perKey="venue_side_jobs_pp" perDefault={5} label="jobs" testid="venue-side-jobs-paged" render={(slice) => (
                  <ul className="space-y-3">
                    {slice.map((j) => (
                      <li key={j.id}>
                        <Link to={`/hiring/${j.slug}`} className="block group" data-testid={`venue-side-job-${j.slug}`}>
                          <p className="text-[10px] uppercase tracking-wider text-rose-300 flex items-center gap-1"><Briefcase size={10} /> {j.job_category || "Job"}</p>
                          <p className="font-semibold text-sm text-white group-hover:text-rose-300 leading-snug mt-0.5">{j.title}</p>
                          {(j.job_type || j.pay_range) && <p className="text-xs text-zinc-500 mt-0.5">{[j.job_type, j.pay_range].filter(Boolean).join(" · ")}</p>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )} />
              </div>
            )}

            {/* 5. Contact & Hours */}
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-4">
              <h3 className="font-display font-bold text-lg">Contact & Hours</h3>
              {detail(MapPin, "Address", v.address)}
              {detail(Phone, "Phone", v.phone)}
              {detail(Mail, "Email", v.email)}
              {detail(Globe, "Website", v.website)}
              {detail(Clock, "Hours", v.hours)}
              {detail(Navigation, "Parking", v.parking_info)}
            </div>

            {/* 6. Past Events — with pagination (kept between Contact & Map for continuity) */}
            {v.past_events?.length > 0 && (
              <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="venue-side-past">
                <h3 className="font-display font-bold text-lg mb-4">Past Events</h3>
                <Paged items={v.past_events} perKey="venue_side_past_pp" perDefault={6} label="events" testid="venue-side-past-paged" render={(slice) => (
                  <ul className="space-y-2.5">
                    {slice.map((e) => (
                      <li key={e.id}>
                        <Link to={`/events/${e.slug}`} className="block group text-sm" data-testid={`venue-side-past-${e.slug}`}>
                          <span className="text-zinc-200 group-hover:text-rose-300">{e.title}</span>
                          <span className="text-zinc-500"> · {fmtDate(e.event_date)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )} />
              </div>
            )}

            {/* 7. Map & Directions */}
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="venue-side-map">
              <h3 className="font-display font-bold text-lg mb-4">Map & Directions</h3>
              <div className="rounded-xl overflow-hidden border border-white/10">
                <iframe title="map" src={MAP(v.address || `${v.city}, ${v.state}`)} className="w-full h-56 grayscale invert-[.9]" loading="lazy" />
              </div>
              <a href={MAP(v.address || `${v.city}, ${v.state}`)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-rose-300 hover:text-rose-200"><Navigation size={12} /> Get directions</a>
            </div>
          </aside>
      </div>

      {/* Mobile sticky bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass sticky-bar px-5 py-3 flex items-center gap-3" data-testid="venue-mobile-bar">
        <a href={MAP(v.address)} target="_blank" rel="noreferrer" className="flex-1 text-center bg-rose-500 font-bold py-3 rounded-xl text-sm"><Navigation className="inline mr-1" size={15} /> Directions</a>
        {v.phone && <a href={`tel:${v.phone}`} className="px-5 py-3 glass rounded-xl"><Phone size={16} /></a>}
      </div>
    </div>
  );
}
